/**
 * PageLoadingFallback -- Suspense fallback for lazy-loaded pages.
 *
 * Design: iOS 26 minimal. Subtle centered spinner, no layout shift, no
 * skeleton placeholders (which would jump when the real page mounts at a
 * different height). Sidebar + topbar remain visible above this component --
 * only the main content area shows the fallback.
 *
 * Visible only during async chunk download. On fast networks (cache hit),
 * the fallback often renders <16ms and is barely perceptible. The
 * `animate-fade-in` (existing utility in tailwind.config) softens the
 * spinner appearance for slow networks where the fallback is visible
 * longer.
 *
 * Threat model -- display-only:
 *   - No user input surface
 *   - No data leaks (zero PII rendered)
 *   - No external resources fetched
 *
 * Bundle cost: ~0.5 KB gzipped (single component, no dependencies beyond
 * lucide-react Loader2 which is already in the icons vendor chunk).
 */

import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";

export function PageLoadingFallback() {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center",
        "min-h-[50vh] gap-4",
        "animate-fade-in",
      )}
      role="status"
      aria-live="polite"
      aria-label={t("common.loading", { defaultValue: "Loading" })}
    >
      {/* Liquid Glass spinner pill */}
      <div
        className={cn(
          "inline-flex items-center justify-center",
          "h-12 w-12 rounded-2xl",
          "bg-white/60 backdrop-blur-xl",
          "ring-1 ring-zinc-900/[0.04]",
          "shadow-[0_2px_12px_rgba(0,0,0,0.04)]",
        )}
      >
        <Loader2
          className="h-5 w-5 animate-spin text-zinc-400"
          aria-hidden="true"
        />
      </div>

      {/* Subtle text -- only visible on slow networks where fallback persists */}
      <span className="text-[13px] text-zinc-400 font-medium">
        {t("common.loading_page", { defaultValue: "Loading page..." })}
      </span>
    </div>
  );
}
